import React, { useEffect, useState } from 'react';
import api, { getAvatarUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePermission, Can } from '../lib/permissions';
import Portal from '../components/Portal';
import { UserPlus, X, Copy, Check, Search } from 'lucide-react';

export default function Team() {
  const { user } = useAuth();
  const { has } = usePermission();
  const canAssignRole = has('users.assign_role');
  const canDeactivate = has('users.deactivate');
  const canInvite = has('invitations.create');
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [depts, setDepts] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const load = async () => {
    const [m, i, r, d] = await Promise.all([
      api.get('/team'),
      api.get('/invitations').catch(() => ({ data: { invitations: [] } })),
      has('roles.view') ? api.get('/roles') : Promise.resolve({ data: { roles: [] } }),
      has('departments.view') ? api.get('/settings/departments') : Promise.resolve({ data: { departments: [] } }),
    ]);
    setMembers(m.data.members); setInvites(i.data.invitations || []);
    setRoles(r.data.roles || []); setDepts(d.data.departments || []);
  };
  useEffect(() => { load(); }, []);

  const updateMemberRole = async (userId, roleId) => { await api.patch(`/users/${userId}/role`, { role_id: roleId }); load(); };
  const updateMemberDept = async (userId, deptId) => { await api.patch(`/team/${userId}`, { department_id: deptId || null }); load(); };
  const updateMember = async (id, patch) => { await api.patch(`/team/${id}`, patch); load(); };
  const revoke = async (id) => { await api.delete(`/invitations/${id}`); load(); };
  
  const deleteMember = async (id) => {
    if (!window.confirm('Are you sure you want to completely delete this member?')) return;
    try {
      await api.delete(`/team/${id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete member');
    }
  };

  const filteredMembers = members.filter(m => (m.full_name || '').toLowerCase().includes(searchQuery.toLowerCase()) || (m.email || '').toLowerCase().includes(searchQuery.toLowerCase()));
  const activeUsers = filteredMembers.filter(m => m.is_active && m.status !== 'deleted' && m.status !== 'pending');
  const inactiveUsers = filteredMembers.filter(m => !m.is_active && m.status !== 'deleted' && m.status !== 'pending');
  const pendingUsers = filteredMembers.filter(m => m.status === 'pending');
  const deletedUsers = filteredMembers.filter(m => m.status === 'deleted');
  const filteredInvites = invites.filter(i => (i.email || '').toLowerCase().includes(searchQuery.toLowerCase()));

  const renderMemberRow = (m) => {
    const currentRole = roles.find((r) => r.system_key === m.role);
    const isDeleted = m.status === 'deleted';
    
    return (
      <div key={m.id} className={`flex items-center gap-3 p-2 rounded hover:bg-slate-50 ${isDeleted ? 'opacity-50 grayscale' : ''}`} data-testid={`member-row-${m.id}`}>
        <div className="tm-avatar" style={{ background: m.avatar_url ? `url(${getAvatarUrl(m.avatar_url)}) center/cover` : (m.avatar_color || 'var(--trello-text-muted)') }}>
          {!m.avatar_url && (m.full_name || m.email).slice(0, 2).toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {m.full_name} {m.id === user.id && <span style={{ color: 'var(--trello-text-muted)', fontWeight: 400 }}>(you)</span>}
            {m.status === 'pending' && (
              <span className="tm-badge" style={{ background: 'var(--trello-list-bg)', color: '#8F6B00', border: '1px solid #FFE0B2', fontSize: 10, marginLeft: 8, padding: '2px 6px', borderRadius: 4, textTransform: 'none', fontWeight: 600 }}>
                Pending Approval
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>{m.email}</div>
        </div>
        {has('departments.view') && depts.length > 0 && !isDeleted && (
          <select className="tm-input" style={{ width: 160 }}
            value={m.department_id || ''}
            onChange={(e) => updateMemberDept(m.id, e.target.value)}
            disabled={!has('users.edit') || m.id === user.id || m.status === 'pending'}
            data-testid={`dept-${m.id}`}>
            <option value="">— No department —</option>
            {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
        {canAssignRole && m.id !== user.id && roles.length && !isDeleted ? (
          <select className="tm-input" style={{ width: 160 }}
            value={m.status === 'pending' ? '' : (currentRole?.id || '')}
            onChange={(e) => { if (e.target.value) updateMemberRole(m.id, e.target.value); }}
            data-testid={`role-${m.id}`}>
            {m.status === 'pending' && <option value="">— Assign Role to Approve —</option>}
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        ) : (
          <span className={`tm-badge tm-badge-${m.role}`} style={{ background: currentRole?.color, color: '#fff' }}>{currentRole?.name || m.role}</span>
        )}
        {canDeactivate && m.id !== user.id && !isDeleted && (
          <button className="tm-btn-ghost" onClick={() => updateMember(m.id, { is_active: !m.is_active })} data-testid={`toggle-active-${m.id}`}>
            {m.is_active ? 'Active' : 'Inactive'}
          </button>
        )}
        {has('users.edit') && m.id !== user.id && !isDeleted && (
          <button className="tm-btn-ghost" onClick={() => deleteMember(m.id)} data-testid={`delete-member-${m.id}`} style={{ color: '#dc2626' }}>
            Delete
          </button>
        )}
      </div>
    );
  };

  const Section = ({ title, count, children }) => {
    if (count === 0) return null;
    return (
      <div className="tm-stat-card" style={{ borderLeft: 'none', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {title} <span className="text-slate-400 text-xs font-normal bg-slate-100 px-2 py-0.5 rounded-full">{count}</span>
        </h3>
        <div className="space-y-1">
          {children}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="team-page">
      <div className="flex items-center justify-between mb-6" style={{ color: 'var(--trello-text)' }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Users</h1>
          <p style={{ opacity: 0.85, marginTop: 4 }}>{members.length} members · {invites.filter(i => i.status === 'pending').length} pending invites</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input 
              type="text" 
              placeholder="Search members..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="tm-input w-64"
              style={{ paddingLeft: 36 }}
            />
          </div>
          <Can perm="invitations.create">
            <button className="tm-btn" onClick={() => setShowInvite(true)} data-testid="invite-btn" style={{ padding: '8px 16px', borderRadius: 10 }}>
              <UserPlus size={16} style={{ display: 'inline', verticalAlign: -3, marginRight: 4 }} />Invite member
            </button>
          </Can>
        </div>
      </div>

      <Section title="Active Users" count={activeUsers.length}>
        {activeUsers.map(renderMemberRow)}
      </Section>
      
      <Section title="Inactive Users" count={inactiveUsers.length}>
        {inactiveUsers.map(renderMemberRow)}
      </Section>

      <Section title="Pending for Approval" count={pendingUsers.length}>
        {pendingUsers.map(renderMemberRow)}
      </Section>

      <Section title="Invited" count={filteredInvites.length}>
        {filteredInvites.map(i => (
          <div key={i.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50" data-testid={`invite-row-${i.id}`}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{i.email}</div>
              <div style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>as {i.role} · invited by {i.invited_by_name}</div>
            </div>
            <span className={`tm-badge`} style={{
              background: i.status === 'pending' ? '#FFFAE6' : i.status === 'accepted' ? '#E3FCEF' : '#EBECF0',
              color: i.status === 'pending' ? '#FF8B00' : i.status === 'accepted' ? '#006644' : 'var(--trello-text-muted)'
            }}>{i.status}</span>
            {i.status === 'pending' && has('invitations.revoke') && (
              <button className="tm-btn-ghost" onClick={() => revoke(i.id)} data-testid={`revoke-${i.id}`}><X size={16} /></button>
            )}
          </div>
        ))}
      </Section>

      <Section title="Deleted Users" count={deletedUsers.length}>
        {deletedUsers.map(renderMemberRow)}
      </Section>
      {showInvite && canInvite && <InviteModal roles={roles.filter((r) => r.is_active)} onClose={() => setShowInvite(false)} onSaved={() => { setShowInvite(false); load(); }} userRole={user.role} />}
    </div>
  );
}

function InviteModal({ roles, onClose, onSaved, userRole }) {
  const [mode, setMode] = useState('single'); // single | bulk
  const [form, setForm] = useState({ email: '', role: 'team' });
  const [bulk, setBulk] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState(-1);

  const submitSingle = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post('/invitations', form);
      setResults([{ email: form.email, ...data }]);
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  const submitBulk = async () => {
    const emails = bulk.split(/[\s,;\n]+/).map((e) => e.trim()).filter((e) => e && e.includes('@'));
    if (!emails.length) return alert('Paste at least one email');
    setBusy(true);
    const out = [];
    for (const email of emails) {
      try {
        const { data } = await api.post('/invitations', { email, role: form.role });
        out.push({ email, ...data });
      } catch (err) { out.push({ email, error: err.response?.data?.error || 'Failed' }); }
    }
    setResults(out);
    setBusy(false);
  };

  const copyUrl = (url, idx) => { navigator.clipboard.writeText(url); setCopied(idx); setTimeout(() => setCopied(-1), 1500); };

  return (
    <Portal>
    <div className="tm-modal-overlay" onClick={onClose}>
      <div className="tm-modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{results ? 'Invitations sent' : 'Invite members'}</h2>
          <button onClick={onClose} className="tm-btn-ghost"><X size={18} /></button>
        </div>
        {!results ? (
          <>
            <div className="flex gap-2 mb-3">
              <button className={mode === 'single' ? 'tm-btn' : 'tm-btn-ghost'} onClick={() => setMode('single')} data-testid="invite-mode-single">Single</button>
              <button className={mode === 'bulk' ? 'tm-btn' : 'tm-btn-ghost'} onClick={() => setMode('bulk')} data-testid="invite-mode-bulk">Bulk</button>
            </div>
            {mode === 'single' ? (
              <form onSubmit={submitSingle} className="space-y-3">
                <div><label className="tm-label">Email</label>
                  <input data-testid="invite-email" className="tm-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
                <RoleSelect roles={roles} value={form.role} onChange={(v) => setForm({ ...form, role: v })} userRole={userRole} />
                <button type="submit" className="tm-btn w-full" disabled={busy} data-testid="invite-submit">{busy ? 'Sending…' : 'Send invitation'}</button>
              </form>
            ) : (
              <div className="space-y-3">
                <div><label className="tm-label">Emails (one per line or comma-separated)</label>
                  <textarea className="tm-input" rows={6} value={bulk} onChange={(e) => setBulk(e.target.value)}
                    placeholder="alice@company.com&#10;bob@company.com&#10;charlie@company.com"
                    data-testid="invite-bulk" />
                </div>
                <RoleSelect roles={roles} value={form.role} onChange={(v) => setForm({ ...form, role: v })} userRole={userRole} />
                <button className="tm-btn w-full" onClick={submitBulk} disabled={busy} data-testid="invite-bulk-submit">
                  {busy ? 'Sending…' : `Send invitations`}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-2" style={{ maxHeight: 400, overflowY: 'auto' }}>
            {results.map((r, i) => (
              <div key={i} className="p-2 rounded" style={{ border: '1px solid var(--trello-border)' }} data-testid={`invite-result-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <b style={{ fontSize: 13 }}>{r.email}</b>
                  {r.error ? <span style={{ color: '#DE350B', fontSize: 12 }}>{r.error}</span>
                    : <span style={{ fontSize: 12, color: r.email_status === 'sent' ? '#519839' : '#D29034' }}>{r.email_status === 'sent' ? '✅ emailed' : '⚠ copy link manually'}</span>}
                </div>
                {r.invitation_url && (
                  <div className="flex gap-1">
                    <input className="tm-input" readOnly value={r.invitation_url} style={{ fontSize: 11 }} />
                    <button className="tm-btn-ghost" onClick={() => copyUrl(r.invitation_url, i)}>{copied === i ? <Check size={14} /> : <Copy size={14} />}</button>
                  </div>
                )}
              </div>
            ))}
            <button className="tm-btn w-full mt-2" onClick={onSaved} data-testid="invite-done">Done</button>
          </div>
        )}
      </div>
    </div>
    </Portal>
  );
}

function RoleSelect({ roles, value, onChange, userRole }) {
  // Managers can only invite team members
  const allowed = userRole === 'leader' ? roles : roles.filter((r) => r.system_key === 'team');
  return (
    <div><label className="tm-label">Role</label>
      <select className="tm-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid="invite-role">
        {allowed.map((r) => <option key={r.id} value={r.system_key || r.id}>{r.name}</option>)}
      </select>
    </div>
  );
}
